import { getActiveDevice } from '@/input/active-device';
import { sfx } from '@/audio/sfx';
import { bestSocket, compareEquip } from './shop-compare';
import { ShopFly } from './shop-fly';
import type { HangarHooks } from './shop-hangar';
import { itemPos } from './shop-layout';
import type { PromptAction } from './shop-prompt';
import { bagWeapons, type FocusItem, type ShopCallbacks, type ShopState } from './shop-types';
import { scrapGems } from './weapon-labels';

/**
 * Hangar interaction state (docs/09 wave 4), split from upgrade-screen:
 * - the TARGET socket a bag weapon flies into: a socket the player picked
 *   (pinned), else the first empty one, else the best swap;
 * - scrapping a bag weapon is press-twice (first press arms: the tile turns
 *   red and shows +N gems) — no text dialog;
 * - the glyph flight bag ⇄ socket on every equip.
 */
export class HangarInteraction {
  /** Socket the player selected first (-1 = automatic) */
  pinned = -1;
  /** Bag weapon whose scrap is armed */
  armed: string | null = null;
  readonly fly = new ShopFly();
  /** Design-space item centres of the active layout (desktop / compact) */
  posOf: (item: FocusItem, s: ShopState) => { x: number; y: number } = itemPos;

  constructor(private readonly callbacks: ShopCallbacks) {}

  reset(): void {
    this.pinned = -1;
    this.armed = null;
  }

  /** Opening focus: a waiting crate, else a NEW weapon that would help. */
  firstFocus(s: ShopState): FocusItem | null {
    if (s.pending) return { kind: 'weapon', weaponId: s.pending };
    const better = bagWeapons(s).find((id) => {
      if (!s.fresh.includes(id)) return false;
      const { before, after } = compareEquip(s, id, this.targetFor(s, id));
      return after.power > before.power;
    });
    return better ? { kind: 'weapon', weaponId: better } : null;
  }

  targetFor(s: ShopState, weaponId: string): number {
    return this.pinned >= 0 && this.pinned < s.equipCapacity ? this.pinned : bestSocket(s, weaponId);
  }

  private isBagOrCrate(s: ShopState, item: FocusItem | undefined): item is { kind: 'weapon'; weaponId: string } {
    return item?.kind === 'weapon' && (s.pending === item.weaponId || bagWeapons(s).includes(item.weaponId));
  }

  /** Builder hooks for the hangar panel around the focused item. */
  hooks(
    s: ShopState, sel: FocusItem | undefined,
    base: Pick<HangarHooks, 'isSel' | 'interactive'>,
    actions: Pick<HangarHooks, 'scrap' | 'swap'>,
  ): HangarHooks {
    const weapon = this.isBagOrCrate(s, sel) ? sel.weaponId : null;
    const target = weapon ? this.targetFor(s, weapon) : this.pinned;
    let compare: HangarHooks['compare'] = null;
    if (weapon) compare = { weaponId: weapon, socket: target };
    else if (sel?.kind === 'socket' && sel.index < s.equipCapacity) compare = { socket: sel.index };
    else if (this.pinned >= 0) compare = { socket: this.pinned };
    return {
      ...base,
      targetFor: (id) => this.targetFor(s, id),
      targetSocket: target,
      armed: this.armed,
      scrapBadges: getActiveDevice() === 'touch',
      ...actions,
      compare,
    };
  }

  /** Ⓐ on a hangar item. @returns the item to focus next (null = keep). */
  activate(s: ShopState, item: FocusItem): FocusItem | null {
    this.armed = null;
    if (item.kind === 'socket') {
      if (item.index >= s.equipCapacity) {
        this.callbacks.hangar({ kind: 'buySocket' });
        return null;
      }
      // Aim at this socket, then hop to the bag (or the waiting crate)
      this.pinned = this.pinned === item.index ? -1 : item.index;
      sfx.uiMove();
      if (this.pinned < 0) return null;
      const next = s.pending ?? bagWeapons(s)[0];
      return next ? { kind: 'weapon', weaponId: next } : null;
    }
    if (item.kind === 'bagBuy') {
      this.callbacks.hangar({ kind: 'buyBag' });
      return null;
    }
    if (item.kind === 'weapon') return this.equip(s, item.weaponId);
    return null;
  }

  /** Bag weapon / crate → its target socket, with the glyph flight. */
  private equip(s: ShopState, weaponId: string): FocusItem {
    const socket = this.targetFor(s, weaponId);
    const old = s.equipped[socket] ?? null;
    const from = this.posOf({ kind: 'weapon', weaponId }, s);
    const to = this.posOf({ kind: 'socket', index: socket }, s);
    this.callbacks.hangar({ kind: 'equip', weaponId, socket });
    this.fly.launch(weaponId, from, to);
    if (old) this.fly.launch(old, to, from);
    this.pinned = -1;
    // Keep the cursor where the hand is: on the weapon that came back
    return old ? { kind: 'weapon', weaponId: old } : { kind: 'socket', index: socket };
  }

  /** Where the hand rests after `weaponId` is scrapped: a bag neighbour, else socket 1. */
  afterScrap(s: ShopState, weaponId: string): FocusItem {
    const bag = bagWeapons(s).filter((id) => id !== weaponId);
    const i = Math.max(0, bagWeapons(s).indexOf(weaponId) - 1);
    const next = bag[Math.min(i, bag.length - 1)];
    return next ? { kind: 'weapon', weaponId: next } : { kind: 'socket', index: 0 };
  }

  /**
   * Ⓧ / ✕ on a hangar item: the waiting crate scraps at once (it is not
   * in the build yet); a bag weapon arms first, the second press scraps.
   * @returns false when the item has nothing to scrap
   */
  scrap(s: ShopState, item: FocusItem | undefined): boolean {
    if (!this.isBagOrCrate(s, item)) return false;
    const id = item.weaponId;
    if (s.pending === id || this.armed === id) {
      this.armed = null;
      this.callbacks.hangar({ kind: 'scrap', weaponId: id });
    } else {
      this.armed = id;
      sfx.uiMove();
    }
    return true;
  }

  /** Footer dock: the focused item's ONE primary action + live secondaries. */
  prompt(s: ShopState, sel: FocusItem | undefined): {
    primary: PromptAction; scrap: boolean; scrapValue: number; info: boolean;
  } {
    const scrap = this.isBagOrCrate(s, sel);
    const info = scrap || (sel?.kind === 'socket' && s.equipped[sel.index] !== undefined);
    const base = { scrap, scrapValue: scrap ? scrapGems(sel.weaponId) : 0, info };
    if (!sel) return { primary: { kind: 'none' }, ...base };
    switch (sel.kind) {
      case 'card': {
        const oc = s.offer[sel.index];
        return {
          primary: oc && !oc.bought ? { kind: 'buy', price: oc.price, red: oc.currency === 'red' } : { kind: 'none' },
          ...base,
        };
      }
      case 'socket':
        return {
          primary: sel.index < s.equipCapacity
            ? { kind: 'pin', socket: sel.index }
            : { kind: 'buy', price: s.socketPrice ?? 0, red: true },
          ...base,
        };
      case 'weapon':
        return { primary: { kind: 'equip', socket: this.targetFor(s, sel.weaponId) }, ...base };
      case 'bagBuy': return { primary: { kind: 'buy', price: s.bagPrice ?? 0, red: false }, ...base };
      case 'reroll': return { primary: { kind: 'reroll', price: s.rerollPrice }, ...base };
      case 'heal': return { primary: { kind: 'buy', price: s.healPrice, red: true }, ...base };
      case 'buyArmor':
        return {
          primary: s.armorItems > 0 ? { kind: 'use' } : { kind: 'buy', price: s.armorPrice, red: false },
          ...base,
        };
      case 'continue': return { primary: { kind: 'continue' }, ...base };
      case 'chip':
      case 'tab':
        return { primary: { kind: 'none' }, ...base };
    }
  }
}
